import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { clearAuth, isAuthenticated } from '../../services/auth';
import { ApiError, refreshSession } from '../../services/api';

interface ProtectedRouteProps {
  children: ReactNode;
}

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(() => isAuthenticated());
  const [sessionError, setSessionError] = useState<string | null>(null);
  const isMountedRef = useRef(true);

  const isApiError = (error: unknown): error is ApiError => error instanceof ApiError;

  const validateSession = useCallback(async () => {
    setIsLoading(true);
    setSessionError(null);

    try {
      await refreshSession();
      if (isMountedRef.current) {
        setAuthenticated(true);
      }
    } catch (error: unknown) {
      if (!isMountedRef.current) return;

      if (isApiError(error) && error.status === 401) {
        clearAuth();
      }
      setAuthenticated(false);
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    validateSession();
    return () => {
      isMountedRef.current = false;
    };
  }, [validateSession]);

  if (isLoading) {
    return <p>{t('common.loading')}</p>;
  }

  if (sessionError) {
    return (
      <div className="auth-error">
        <p>{sessionError}</p>
        <button type="button" onClick={validateSession} className="btn btn-primary">
          {t('auth.retry', 'Retry')}
        </button>
      </div>
    );
  }

  if (!authenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
